import { ColumnDef } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'

import { BoxWithLogo, FlexBox } from 'components/Box'
import { LastModifiedCell } from 'components/Table'
import { COLUMN_SIZE } from 'components/Table/constants'
import { bidderLogos } from 'constants/bidderLogos'
import { Alert } from 'features/alerts/types/alert'
import { AdUnitBidderAlert } from 'features/globalSetup/api/types/adUnitBidderAlert'
import { AdUnitBidderSlice } from 'features/inventory/ad-unit/forms/PrebidStackForm'
import { AdUnitBidderAlerts } from '../AdUnitBidderAlerts'
import { ActionsCell } from './components/ActionsCell'
import { CsEnabledCell } from './components/CsEnabledCell'
import { S2sEnabledCell } from './components/S2sEnabledCell'

export const useColumns = (): ColumnDef<AdUnitBidderSlice>[] => {
  const { t } = useTranslation('features/adUnit')

  return [
    {
      accessorKey: 'name',
      cell: ({
        row: {
          original: { name },
        },
      }) => <BoxWithLogo logo={{ size: 'small', svg: bidderLogos[name] }}>{name}</BoxWithLogo>,
      header: t('form.prebidStack.bidders.list.header.name'),
    },

    {
      accessorKey: 'code',
      header: t('form.prebidStack.bidders.list.header.code'),
    },

    {
      accessorKey: 'csEnabled',
      cell: ({
        row: {
          original: { accountType, alerts, csRequiredParams, id, workspaceBidderStatus },
        },
      }) => (
        <FlexBox axis='x'>
          <CsEnabledCell
            {...{ accountType, csRequiredParams, id, status, workspaceBidderStatus }}
          />

          {alerts.length > 0 && (
            <AdUnitBidderAlerts
              alerts={alerts.filter((alert: Alert<AdUnitBidderAlert>): boolean =>
                alert.name.startsWith('cs'),
              )}
            />
          )}
        </FlexBox>
      ),
      header: t('form.prebidStack.bidders.list.header.csEnabled'),
      meta: {
        centered: true,
        noMemo: true,
        nonClickable: true,
      },
    },

    {
      accessorKey: 's2sEnabled',
      cell: props => {
        const {
          row: {
            original: {
              accountType,
              alerts,
              id,
              s2sAvailable,
              s2sRequiredParams,
              workspaceBidderStatus,
            },
          },
        } = props

        return (
          s2sAvailable && (
            <FlexBox axis='x'>
              <S2sEnabledCell {...{ accountType, id, s2sRequiredParams, workspaceBidderStatus }} />

              {alerts.length > 0 && (
                <AdUnitBidderAlerts
                  alerts={alerts.filter((alert: Alert<AdUnitBidderAlert>): boolean =>
                    alert.name.startsWith('s2s'),
                  )}
                />
              )}
            </FlexBox>
          )
        )
      },
      header: t('form.prebidStack.bidders.list.header.s2sEnabled'),
      meta: {
        centered: true,
        noMemo: true,
        nonClickable: true,
      },
    },

    {
      accessorKey: 'updatedAt',
      cell: props => <LastModifiedCell {...props} />,
      header: t('form.prebidStack.bidders.list.header.updatedAt'),
    },

    {
      accessorKey: 'actions',
      cell: props => <ActionsCell {...props} />,
      header: t('form.prebidStack.bidders.list.header.actions'),
      meta: { nonClickable: true },
      size: COLUMN_SIZE.SM,
    },
  ]
}
